/* eslint-disable camelcase */
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import * as _ from 'lodash';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Subscription } from 'rxjs';
import { TylerMatTableDataSource } from '../../../shared-components/angular-material/extended.class';
import { GeneralConfirmationComponent } from '../../../shared-components/controls/general-confirmation/general-confirmation.component';
import { LookupService } from '../../../shared-components/lookup/lookup.service';
import { SharedService } from '../../../shared-components/shared.service';
import { CustomizationFrameworkService } from '../../../shared-modules/customization-framework/customization-framework.service';
import { DataDefinition, DataDefinitionList } from '../data-definition.model';
import { DataDefinitionService } from '../data-definition.service';
import { AddEditDataDefinitionComponent } from './add-edit-data-definition/add-edit-data-definition.component';

@AutoUnsubscribe({ includeArrays: true })
@Component({
  selector: 'data-definition-static',
  templateUrl: './data-definition-static.component.html',
  styleUrls: ['./data-definition-static.component.css']
})
export class DataDefinitionStaticComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  dataDefList: DataDefinitionList;
  dataDef: DataDefinition[] = [];
  dataSource: TylerMatTableDataSource = new TylerMatTableDataSource(this.dataDef);
  columnsToDisplay: string[];
  subscriptions: Subscription[] = [];
  sequenceChanged: boolean;

  @Input() DataDefList: DataDefinitionList;
  @Input() set addClick(trigger: EventEmitter<any>) {
    this.subscriptions.push(trigger.subscribe(o => {
      this.addDataDef();
    }));
  }

  @Input() set saveSequenceClick(trigger: EventEmitter<any>) {
    this.subscriptions.push(trigger.subscribe(o => {
      this.saveDataDefinition(this.dataDefList.Enum, this.dataDef);
    }));
  }

  @ViewChild(MatSort, { static: true }) sort: MatSort;

  // --------------LifeCycle---------------------------------------
  constructor(
    private dialog: MatDialog,
    private sharedService: SharedService,
    private lookupService: LookupService,
    private dataDefinitionService: DataDefinitionService,
    private customizationFrameworkService: CustomizationFrameworkService
  ) { }

  ngOnInit() { }

  ngOnChanges() {
    if (this.DataDefList) {
      this.dataDefList = this.DataDefList;
      this.getDataDefinition();
    }
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy() { }

  getDataDefinition() {
    this.setColumnsToDisplay();
    this.subscriptions.push(this.dataDefinitionService.getDataDefinition(this.dataDefList.Enum, 0, 0, 0).subscribe(res => {
      if (res && res.length > 0) {
        this.dataDef = res;
      } else {
        this.dataDef = [];
      }
      this.customizationFrameworkService.dataDefinitionListValues[this.dataDefList.Enum] = this.dataDef;
      this.dataSource.data = this.dataDef;
    }));
  }

  addDataDef() {
    const newDataDef = new DataDefinition();
    newDataDef.LookupListId = this.dataDefList.Id;
    newDataDef.Inactive = false;

    const dialogRef = this.dialog.open(AddEditDataDefinitionComponent, {
      panelClass: 'tyl-mat-dialog',
      disableClose: true
    });
    dialogRef.componentInstance.dataDefList = this.dataDefList;
    dialogRef.componentInstance.dataDef = this.dataDef;
    dialogRef.componentInstance.selectedDataDef = newDataDef;

    this.subscriptions.push(dialogRef.afterClosed().subscribe(action => {
      if (action == 'save') {
        const dataDefRows: DataDefinition[] = [];
        dataDefRows.push(dialogRef.componentInstance.selectedDataDef);
        this.saveDataDefinition(this.dataDefList.Enum, dataDefRows);
      }
    }));
  }

  editClick(dataDef: DataDefinition) {
    const dialogRef = this.dialog.open(AddEditDataDefinitionComponent, {
      panelClass: 'tyl-mat-dialog',
      disableClose: true
    });
    dialogRef.componentInstance.dataDefList = this.dataDefList;
    dialogRef.componentInstance.dataDef = this.dataDef;
    dialogRef.componentInstance.selectedDataDef = _.cloneDeep(dataDef);

    this.subscriptions.push(dialogRef.afterClosed().subscribe(action => {
      if (action == 'save') {
        const dataDefRows: DataDefinition[] = [];
        dataDefRows.push(dialogRef.componentInstance.selectedDataDef);
        this.saveDataDefinition(this.dataDefList.Enum, dataDefRows);
      }
    }));
  }

  saveDataDefinition(enum_value: string, dataDefRows: DataDefinition[]) {
    this.subscriptions.push(this.dataDefinitionService.saveDataDefinition(enum_value, dataDefRows).subscribe(result => {
      if (result) {
        // clear in-memory value to force data def dropdown to get new values
        this.customizationFrameworkService.dataDefinitionListValues[enum_value] = null;
        if (this.customizationFrameworkService.dataDefinitionLists.find(o => o.Enum == enum_value)) {
          this.customizationFrameworkService.dataDefinitionLists.find(o => o.Enum == enum_value).DataDefinitions = null;
        }
        this.sharedService.showGoodSnackbar();
        this.getDataDefinition();
      }
      else {
        this.sharedService.showBadSnackbar();
      }
    }));
  }

  clearFilter() {
    this.dataSource.removeAllFilters();
  }

  setColumnsToDisplay() {
    this.columnsToDisplay = [];
    if (!this.dataDefList.ReadOnly && this.dataDefList.IsSortable) {
      this.columnsToDisplay.push('drag-handle');
    }
    if (this.dataDefList.ShowCodeField) {
      this.columnsToDisplay.push('Code');
    }
    if (this.dataDefList.ShowDescriptionField) {
      this.columnsToDisplay.push('Description');
    }
    if (this.dataDefList.ShowAbbreviationField) {
      this.columnsToDisplay.push('Abbreviation');
    }
    if (this.dataDefList.ShowUsedForStateReporting) {
      this.columnsToDisplay.push('IsStateReporting');
    }
    if (this.dataDefList.ShowDefaultField) {
      this.columnsToDisplay.push('IsDefault');
    }
    if (this.dataDefList.ShowStatusField) {
      this.columnsToDisplay.push('Inactive');
    }
    if (this.dataDefList.UserCanEdit && !this.dataDefList.ReadOnly) {
      this.columnsToDisplay.push('Actions');
    }
  }

  rowDrop(event: CdkDragDrop<any, any>) {
    // PRE: one of the row is dragged and dropped within the table
    // POST: rearranges the order of the sort fields and sets the query to changed
    // NOTE: we have to manually obtain the previous index because the event.previousIndex is wrong

    const field = event.item.data;
    const previousIndex = this.dataDef.indexOf(field);
    moveItemInArray(this.dataDef, previousIndex, event.currentIndex);
    this.dataDef.forEach((ec, index) => {
      ec.Sequence = index;
    });
    this.dataSource.data = this.dataDef;
    this.sequenceChanged = true;
  }
}